import React, {useRef} from 'react'
import {Box, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {ChevronLeftIcon, ChevronRightIcon} from '@salesforce/retail-react-app/app/components/icons'

import {getGDIHideStyles} from '../../utils/gdi-styles'
import {getAllExpandedFromContentArea} from '../../utils/gdi-content-area'
import {mapResponsiveValue} from '../../utils/gdi-responsive'
import {renderComponentsSwitch} from '../../../utils/contentRenderer'
import {useHeaderContext} from '../../../../../contexts/header-context'
import {categoryUrlBuilder} from '../../../../../utils/url'
import {
    GDIHoverRevealContext,
    GDIGridColumnsContext,
    GDIProductTogglesContext,
    useGDIGridColumns
} from '../../utils/gdi-link-context'
import {useDragScroll} from '../../utils/use-drag-scroll'
import {useInfiniteLoop, maxResponsive} from '../../utils/use-infinite-loop'

import {
    buildSlideWidth,
    buildSpanWidth,
    getTileColSpan,
    slideMeta,
    getSlideStride,
    FLUID_INSET,
    isRecentlyViewedSource,
    dynamicSourceToAIModel
} from './helpers'
import {ArrowButton} from './arrows'
import {useCarouselGridConfig} from './use-carousel-grid-config'
import {useEdgeState} from './use-edge-state'
import {useAutoplay} from './use-autoplay'
import {useVickyTiles} from './use-vicky-tiles'
import {useRecentlyViewedTiles} from './use-recently-viewed-tiles'
import {useProductTileToggles} from './use-product-tile-toggles'
import {useViewportBleed} from './use-viewport-bleed'
import {useGDIDynamicContentContext} from '../../utils/gdi-dynamic-content-context'
import {useCategory, useProduct} from '@salesforce/commerce-sdk-react'

const PLP_SUB_CATEGORIES_DYNAMIC_CONTENT = 'Sub-categories Links'
const PDP_MORE_TO_DISCOVER_DYNAMIC_CONTENT = 'More to Discover'
const GDI_PDP_PAGE_TYPE = 'GDIPDPPage'
const RESPONSIVE_BREAKPOINTS = ['base', 'md', 'lg']

const resolveResponsiveAt = (value, bp, fallback) => {
    if (value && typeof value === 'object') {
        if (value[bp] !== undefined) return value[bp]
        const index = RESPONSIVE_BREAKPOINTS.indexOf(bp)
        for (let i = index - 1; i >= 0; i -= 1) {
            const previous = value[RESPONSIVE_BREAKPOINTS[i]]
            if (previous !== undefined) return previous
        }
        return fallback
    }

    return value ?? fallback
}

const mapCarouselInteraction = (displayTypeByBp, draggableByBp, transform) =>
    Object.fromEntries(
        RESPONSIVE_BREAKPOINTS.map((bp) => [
            bp,
            transform(
                resolveResponsiveAt(displayTypeByBp, bp, 'Grid'),
                resolveResponsiveAt(draggableByBp, bp, true)
            )
        ])
    )

const normalizeProductSku = (value) => {
    if (typeof value === 'string') return value.trim() || null
    return value || null
}

const getPdpProductSku = (product) =>
    normalizeProductSku(product?.id || product?.productId || product?.master?.masterId)

const parseDiscoverCategories = (discoverCategories) => {
    try {
        const parsed = JSON.parse(discoverCategories || '[]')
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const GDICarouselGridBlock = ({
    HideOnDevices,
    HideBlockOn,
    ContentTiles,
    CtaButtonDynamicContent,
    CtaButtonDynamicContentReference,
    CtaButtonManualContent,
    CtaButtonStylesPerViewType,
    ProductCarouselGridManualContent,
    ProductCarouselGridContentTile,
    ProductCarouselGridDynamicContent,
    ProductCarouselGridDynamicContentReference,
    GDICarouselGridBlockStylesPerViewType,
    ProductCarouselGridStylesPerViewType,
    ReverseOrderOnDesktop,
    MaxItemsToShow,
    DisplayTextAndCTAsOnHover,
    ContentTilesStyles,
    _recommendationData
}) => {
    const hideStyles = getGDIHideStyles(HideOnDevices || HideBlockOn)
    const scrollRef = useRef(null)
    /* `category` flows in from the active PLP/PDP page via HeaderContext.
     * Read unconditionally to keep hook order stable; `null` when the block
     * isn't placed on a PLP. */
    const {category} = useHeaderContext() || {}
    const {
        pdpProduct: contextPdpProduct,
        pdpProductSku: contextPdpProductSku,
        contentPageType,
        disableViewportBleed: contextDisableViewportBleed
    } = useGDIDynamicContentContext()
    const isGDIPDPPageContext = contentPageType === GDI_PDP_PAGE_TYPE || Boolean(contextPdpProduct)
    const pdpProduct = isGDIPDPPageContext ? contextPdpProduct : null

    const {
        displayTypeByBp,
        currentDisplayType,
        tilesPerRow,
        tilesPerRowAuto,
        hasPartialTilePeek,
        tilesGap,
        justify,
        ctaAlignSelf,
        showArrows,
        arrowSx,
        arrowDisplay,
        draggable,
        draggableByBp,
        autoplay,
        loop,
        delay,
        tilesToScroll,
        sectionWidth,
        sectionBackgroundColor,
        sectionBackgroundMedia,
        sectionTitle,
        sectionColSpan,
        sectionGridColumn,
        sectionMargin,
        sectionPadding,
        isFluid,
        isFullBleed,
        hasMaxWidth
    } = useCarouselGridConfig(GDICarouselGridBlockStylesPerViewType)

    /* Parent grid's per-breakpoint column count (when this block is itself a
     * tile inside another GDICarouselGridBlock). Needed to decide whether
     * left-side viewport bleed is safe at each breakpoint: if our ColSpan
     * matches/exceeds the parent's TilesPerRow at that breakpoint we span
     * the full parent row, so no left sibling exists to overlap. */
    const parentGridColumns = useGDIGridColumns()
    const isFullWidthInParent = (() => {
        if (!sectionGridColumn) return true // not nested → always treat as full width
        const colsObj =
            parentGridColumns && typeof parentGridColumns === 'object'
                ? parentGridColumns
                : {base: Number(parentGridColumns) || null}
        const spanObj =
            sectionColSpan && typeof sectionColSpan === 'object'
                ? sectionColSpan
                : {base: Number(sectionColSpan) || null}
        const bps = new Set([...Object.keys(colsObj), ...Object.keys(spanObj)])
        const out = {}
        bps.forEach((bp) => {
            const cols = Number(colsObj[bp]) || null
            const span = Number(spanObj[bp]) || null
            // unknown parent columns → assume there could be siblings (false)
            out[bp] = cols ? span >= cols : false
        })
        return out
    })()

    const productToggles = useProductTileToggles(ProductCarouselGridStylesPerViewType)

    /* Aggregate every content-area slot the block exposes. Product source
     * priority is dynamic source first, dynamic reference as the SKU/query for
     * that source, and manual products only when no dynamic product source is
     * configured. */
    const dynamicAIModel = dynamicSourceToAIModel(ProductCarouselGridDynamicContent)
    const authoredDynamicReference = normalizeProductSku(ProductCarouselGridDynamicContentReference)
    const pdpProductSku = isGDIPDPPageContext
        ? normalizeProductSku(contextPdpProductSku) || getPdpProductSku(pdpProduct)
        : null
    const effectiveDynamicReference =
        dynamicAIModel === 'semantic-search'
            ? authoredDynamicReference
            : authoredDynamicReference || pdpProductSku
    const vickyTiles = useVickyTiles(
        ProductCarouselGridDynamicContent,
        _recommendationData,
        effectiveDynamicReference
    )
    const recentlyViewedTiles = useRecentlyViewedTiles(ProductCarouselGridDynamicContent)
    /* CTA reference overrides: when `CtaButtonDynamicContentReference` is
     * authored, its value is used as the category ID (for Sub-categories Links)
     * or product ID (for More to Discover) instead of the current page context. */
    const ctaRefCategoryId =
        CtaButtonDynamicContent === PLP_SUB_CATEGORIES_DYNAMIC_CONTENT
            ? normalizeProductSku(CtaButtonDynamicContentReference)
            : null
    const ctaRefProductId =
        CtaButtonDynamicContent === PDP_MORE_TO_DISCOVER_DYNAMIC_CONTENT
            ? normalizeProductSku(CtaButtonDynamicContentReference)
            : null
    const {data: referencedCtaCategory} = useCategory(
        {parameters: {id: ctaRefCategoryId || '', levels: 1}},
        {enabled: Boolean(ctaRefCategoryId)}
    )
    const {data: referencedCtaProduct} = useProduct(
        {parameters: {id: ctaRefProductId || ''}},
        {enabled: Boolean(ctaRefProductId)}
    )
    /* Measures the distance from the section wrapper's outer edges to the
     * viewport's edges so the inner scroll track can bleed out via measured
     * pixel values (avoids the `calc(50% - 50vw)` pitfalls with scrollbars
     * / ancestor padding). The measurement refresh key includes async data
     * sources (Vicky/Einstein product tiles, referenced category/product CTA
     * sources, active PLP/PDP context) so a carousel that initially renders
     * empty — then mounts after data arrives — re-measures once the wrapper
     * is actually in the DOM. Without this, an initially-empty carousel
     * (e.g. Sub-categories Links waiting for the category fetch) bails out
     * via `limited.length === 0`, never attaches its ref, and the bleed
     * stays at 0 even after content arrives. */
    const dynamicTileCount =
        vickyTiles.length +
        recentlyViewedTiles.length +
        (referencedCtaCategory?.categories?.length || 0) +
        (referencedCtaProduct?.c_discoverCategories ? 1 : 0) +
        (category?.categories?.length || 0) +
        (pdpProduct?.c_discoverCategories ? 1 : 0)
    const {
        ref: bleedRef,
        leftPx: bleedLeft,
        rightPx: bleedRight
    } = useViewportBleed(dynamicTileCount)
    /* ProductCarouselGridManualContent is a plain array of product IDs/SKUs
     * (strings) authored in the CMS. Wrap each as a GDIProductTileBlock
     * pseudo-tile so renderComponentsSwitch picks it up exactly like a
     * Vicky-driven tile. */
    const manualProductTiles = Array.isArray(ProductCarouselGridManualContent)
        ? ProductCarouselGridManualContent.filter((id) => typeof id === 'string' && id).map(
              (id, i) => ({
                  __typename: 'GDIProductTileBlock',
                  ProductId: id,
                  ContentLink: {GuidValue: `manual-${id}-${i}`}
              })
          )
        : []
    const hasDynamicProductSource =
        isRecentlyViewedSource(ProductCarouselGridDynamicContent) || Boolean(dynamicAIModel)
    const dynamicProductTiles = isRecentlyViewedSource(ProductCarouselGridDynamicContent)
        ? recentlyViewedTiles
        : vickyTiles
    const productTiles = hasDynamicProductSource ? dynamicProductTiles : manualProductTiles

    /* Dynamic CTAs derived from the active PLP category. When
     * `CtaButtonDynamicContent === 'Sub-categories Links'` the carousel emits
     * one CTA for the parent category (always first) plus one per child
     * category. Falls back to `CtaButtonManualContent` when the dynamic
     * source is unset / unsupported / there's no PLP category in scope. */
    const buildSubcategoryCtaTile = (cat, suffix) => {
        if (!cat?.id || !cat?.name) return null
        /* `categoryUrlBuilder` reads `c_relativeUrl` (populated by SCAPI on
         * both the active category and its `categories` children) — same
         * source the rest of the storefront uses for category links.
         * Surface it via `resolvedUrl` so `useGetLinkData` returns the
         * SEO-friendly URL exactly like an SSR-resolved authored link. */
        const resolvedUrl = categoryUrlBuilder(cat)
        return {
            __typename: 'GDICtaBlock',
            ContentLink: {GuidValue: `dyn-cta-subcat-${cat.id}-${suffix}`},
            CtaText: cat.name,
            GDICtaBlockLink: [
                {
                    ContentLink: {
                        Expanded: {
                            __typename: 'GDILinkBlock',
                            CategoryId: cat.id,
                            AriaLabel: cat.name,
                            resolvedUrl
                        }
                    }
                }
            ],
            GDICtaBlockStylesPerViewType: CtaButtonStylesPerViewType
        }
    }

    const buildPdpMoreToDiscoverCtaTile = (discoverCategory, index) => {
        const {id, name, url} = discoverCategory || {}
        if (!name || (!id && !url)) return null

        const key = id || `url-${index}`

        return {
            __typename: 'GDICtaBlock',
            ContentLink: {GuidValue: `dyn-cta-pdp-more-to-discover-${key}`},
            CtaText: name,
            GDICtaBlockLink: [
                {
                    ContentLink: {
                        Expanded: {
                            __typename: 'GDILinkBlock',
                            ...(id ? {CategoryId: id, resolvedUrl: url} : {ExternalLink: url}),
                            AriaLabel: name
                        }
                    }
                }
            ],
            GDICtaBlockStylesPerViewType: CtaButtonStylesPerViewType
        }
    }

    const dynamicCtaTiles = (() => {
        if (CtaButtonDynamicContent === PDP_MORE_TO_DISCOVER_DYNAMIC_CONTENT) {
            const sourceProduct = referencedCtaProduct || pdpProduct
            return parseDiscoverCategories(sourceProduct?.c_discoverCategories)
                .map(buildPdpMoreToDiscoverCtaTile)
                .filter(Boolean)
        }

        if (CtaButtonDynamicContent !== PLP_SUB_CATEGORIES_DYNAMIC_CONTENT) return null
        const sourceCategory = referencedCtaCategory || category
        if (!sourceCategory) return null
        const children = Array.isArray(sourceCategory.categories) ? sourceCategory.categories : []
        // No subcategories → suppress the dynamic CTAs entirely (including
        // the parent tile) so the carousel doesn't render a lone parent
        // link as the only CTA.
        if (children.length === 0) return []
        return [
            buildSubcategoryCtaTile(sourceCategory, 'parent'),
            ...children.map((child, i) => buildSubcategoryCtaTile(child, `child-${i}`))
        ].filter(Boolean)
    })()

    const ctaTiles = dynamicCtaTiles || getAllExpandedFromContentArea(CtaButtonManualContent)

    const tiles = [
        ...getAllExpandedFromContentArea(ContentTiles),
        ...productTiles,
        ...ctaTiles,
        ...getAllExpandedFromContentArea(ProductCarouselGridContentTile)
    ]
    const limited =
        typeof MaxItemsToShow === 'number' && MaxItemsToShow > 0
            ? tiles.slice(0, MaxItemsToShow)
            : tiles

    const carouselActive = currentDisplayType === 'Carousel'

    const {atStart, atEnd} = useEdgeState(scrollRef, {
        enabled: carouselActive,
        slidesLength: limited.length
    })
    useAutoplay(scrollRef, {
        enabled: autoplay && carouselActive,
        delay,
        loop,
        tilesToScroll,
        slidesLength: limited.length
    })
    useDragScroll(scrollRef, {enabled: draggable && carouselActive, refreshKey: limited.length})
    useInfiniteLoop(scrollRef, {
        enabled: loop && carouselActive && limited.length > 1,
        slidesLength: limited.length
    })

    if (hideStyles.doNotLoadComponent) return null
    /* Bail only when there are no tiles AND no section-level styling authored.
     * The section can be a standalone backdrop (background color/media) even
     * with no tiles, so we keep rendering the wrapper in that case. */
    const hasSectionStyling =
        sectionBackgroundColor || sectionBackgroundMedia || hasMaxWidth || sectionGridColumn
    if (limited.length === 0 && !hasSectionStyling) return null

    const keyOf = (tile, idx) => tile?.ContentLink?.GuidValue || `gdi-cg-${idx}`
    /* Gap-aware slide width: subtracts (N-1)*gap from 100% so wrapped rows
     * fit exactly N tiles without flow-overflow forcing single-column wrap.
     * In auto mode (TilesPerRow unauthored) slides size to their intrinsic
     * content width and the row scrolls when total width > viewport. */
    const slideWidth = tilesPerRowAuto ? undefined : buildSlideWidth(tilesPerRow, tilesGap)

    /* Cloned head/tail bands for seamless infinite loop — only at carousel bp.
     * Ceil the largest TilesPerRow so fractional values still produce enough
     * clones for backward nav not to clamp at scrollLeft=0. Skip entirely in
     * auto mode: cloning intrinsically-sized tiles would scramble the natural
     * scroll length the loop hook measures. */
    const cloneCount =
        loop && limited.length > 1 && carouselActive && !tilesPerRowAuto
            ? Math.max(1, Math.ceil(maxResponsive(tilesPerRow)))
            : 0
    const headClones = cloneCount > 0 ? limited.slice(-cloneCount) : []
    const tailClones = cloneCount > 0 ? limited.slice(0, cloneCount) : []
    const renderList = [...headClones, ...limited, ...tailClones]

    const scroll = (direction = 1) => {
        const node = scrollRef.current
        if (!node) return
        const stride = getSlideStride(node) * tilesToScroll
        node.scrollBy({left: direction * stride, behavior: 'smooth'})
    }

    /* CSS handles the structural switch between Grid and Carousel — no need
     * to swap component types between breakpoints. */
    const flexWrap = mapResponsiveValue(displayTypeByBp, (dt) =>
        dt === 'Carousel' ? 'nowrap' : 'wrap'
    )
    const overflowX = mapCarouselInteraction(displayTypeByBp, draggableByBp, (dt, canDrag) =>
        dt === 'Carousel' ? (canDrag ? 'auto' : 'hidden') : 'visible'
    )
    const overflowY = mapResponsiveValue(displayTypeByBp, (dt) =>
        dt === 'Carousel' ? 'hidden' : 'visible'
    )
    const scrollSnapType = mapResponsiveValue(displayTypeByBp, (dt) =>
        dt === 'Carousel' ? 'x mandatory' : 'none'
    )
    const cursor = mapCarouselInteraction(displayTypeByBp, draggableByBp, (dt, canDrag) =>
        dt === 'Carousel' && canDrag ? 'grab' : 'auto'
    )
    const activeCursor = mapCarouselInteraction(displayTypeByBp, draggableByBp, (dt, canDrag) =>
        dt === 'Carousel' && canDrag ? 'grabbing' : 'auto'
    )

    /* When this block is a draggable carousel at a given breakpoint, force
     * any nested product-tile image-scroller (data-pt-media-scroller) to
     * give up horizontal scrolling at that breakpoint. The browser routes
     * a touch pan to the nearest ancestor that can scroll on that axis, so
     * removing the inner's horizontal overflow lets the parent carousel
     * receive the swipe instead of the tile's own image row.
     *
     * Note: `touch-action: pan-y` on the inner does NOT work here — per
     * spec, touch-action values intersect down the ancestor chain
     * (pan-y ∩ auto = pan-y), which would block the horizontal pan
     * entirely instead of delegating it. Suppressing overflow is the
     * correct mechanism. */
    const innerScrollerOverflowX = mapCarouselInteraction(
        displayTypeByBp,
        draggableByBp,
        (dt, canDrag) => (dt === 'Carousel' && canDrag ? 'hidden' : 'auto')
    )

    /* In Carousel mode, `justify-content: center` (or `flex-end`) on a
     * horizontally-scrolling row pushes the overflow into the unreachable
     * direction — the first slides end up before `scrollLeft: 0` and can
     * never be brought into view. The CSS `safe` keyword makes the browser
     * fall back to `flex-start` when content overflows, so all slides
     * remain reachable while still centering when there's slack. Grid mode
     * doesn't scroll, so it keeps the raw value. */
    const safeJustify = (() => {
        if (!carouselActive) return justify
        const wrap = (v) => (v === 'center' || v === 'flex-end' ? `safe ${v}` : v)
        if (typeof justify === 'object' && justify) {
            return Object.fromEntries(Object.entries(justify).map(([bp, v]) => [bp, wrap(v)]))
        }
        return wrap(justify)
    })()

    /* ReverseOrderOnDesktop applies the RTL trick only when desktop is in
     * Grid mode — reversing item order in a horizontally-scrolling carousel
     * would flip the scroll direction in a confusing way. */
    const reverseSx =
        ReverseOrderOnDesktop && !carouselActive
            ? {'@media (min-width: 1024px)': {direction: 'rtl', '& > *': {direction: 'ltr'}}}
            : undefined

    const sectionWrapperProps = {
        position: 'relative',
        backgroundColor: sectionBackgroundColor || undefined,
        maxWidth: hasMaxWidth ? sectionWidth : undefined,
        padding: sectionPadding || undefined,
        // `mx` handles system-level horizontal positioning (fluid bleed / centering).
        // `sectionMargin` carries author-defined margins; Chakra's mx (marginLeft +
        // marginRight) overrides the horizontal component of the `margin` shorthand
        // per CSS cascade, so centering/bleed still applies to the horizontal axis.
        margin: sectionMargin || undefined,
        mx: isFluid ? 'calc(50% - 50vw)' : hasMaxWidth ? 'auto' : undefined,
        width: isFluid ? '100vw' : undefined,
        gridColumn: sectionGridColumn || undefined,
        margin: sectionMargin || undefined,
        padding: sectionPadding || undefined,
        /* When the section is itself a tile in a parent grid (authored
         * `ColSpan`, surfaced as `sectionGridColumn`), stretch to fill the
         * parent slide so children with vertical positioning (e.g. a content
         * tile authored with `ContentVerticalPosition: Bottom`) have a real
         * height to anchor against. The outer slide wrapper already passes
         * `flex: 1` to its child, so `height: 100%` here resolves to the
         * row height. Become a flex column so the inner scroll track can
         * flex-grow to fill us. */
        ...(sectionGridColumn ? {height: '100%', display: 'flex', flexDirection: 'column'} : null)
    }

    /* Background media layer — absolutely positioned behind contents.
     * Rendered via the content switch so any media block (image, video,
     * etc.) is supported. */
    const sectionBackgroundLayer = sectionBackgroundMedia ? (
        <Box
            data-gdi-section-bg
            position="absolute"
            inset={0}
            zIndex={0}
            pointerEvents="none"
            overflow="hidden"
            sx={{
                /* Force any rendered media (image/video) to cover the section. */
                '& > *, & img, & video, & picture, & picture > img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                }
            }}
        >
            {renderComponentsSwitch(sectionBackgroundMedia)}
        </Box>
    ) : null

    return (
        <GDIHoverRevealContext.Provider value={!!DisplayTextAndCTAsOnHover}>
            <GDIGridColumnsContext.Provider value={tilesPerRow}>
                <GDIProductTogglesContext.Provider value={productToggles}>
                    <Box
                        ref={bleedRef}
                        data-block-type="GDICarouselGridBlock"
                        sx={hideStyles.sx}
                        width="100%"
                        {...sectionWrapperProps}
                    >
                        {sectionBackgroundLayer}
                        {sectionTitle && renderComponentsSwitch(sectionTitle)}
                        <Flex
                            ref={scrollRef}
                            gap={tilesGap}
                            justifyContent={safeJustify}
                            position="relative"
                            zIndex={1}
                            /* Inner padding only when the section is fluid. In
                             * Carousel mode this is the bleed affordance: tiles
                             * scroll past the gutter and peek in from the
                             * viewport edge. In Grid mode it just keeps the
                             * row visually anchored at FLUID_INSET from each
                             * edge. `full-bleed` gets zero padding so tiles
                             * touch the actual viewport edges. */
                            px={isFullBleed ? 0 : isFluid ? FLUID_INSET : undefined}
                            /* Offsets the snap target so tile 1 snaps to the
                             * padding edge, not the container edge. */
                            scrollPaddingX={isFullBleed ? 0 : isFluid ? FLUID_INSET : undefined}
                            sx={{
                                flexWrap,
                                overflowX,
                                overflowY,
                                scrollSnapType,
                                cursor,
                                '&:active': {cursor: activeCursor},
                                WebkitOverflowScrolling: 'touch',
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': {display: 'none'},
                                /* Edge bleed for constrained (non-fluid)
                                 * carousels — uses MEASURED pixel offsets
                                 * (see useViewportBleed) instead of
                                 * `calc(50% - 50vw)` so it's robust against
                                 * vertical scrollbars and ancestor padding.
                                 * The negative margins extend the scroll
                                 * track to the actual viewport edges; the
                                 * matching `padding-*` (and
                                 * `scroll-padding-*`) make the first/last
                                 * snap position align with the original
                                 * page-content boundary, so on initial
                                 * load tile 1 is flush with surrounding
                                 * page content and the right gutter only
                                 * re-appears when scrolled to the end.
                                 * While scrolling, outgoing/incoming tiles
                                 * travel all the way to the viewport edges.
                                 * App-root `overflow-x: clip`
                                 * (theme/foundations/styles.js) absorbs
                                 * the overflow so this never produces a
                                 * page-level horizontal scrollbar.
                                 *
                                 * When the section sits in a parent grid cell
                                 * (authored ColSpan, surfaced here as
                                 * `sectionGridColumn`), suppress only the LEFT
                                 * bleed — otherwise slides would scroll over
                                 * sibling columns to its left. The right
                                 * bleed is preserved so a right-aligned cell
                                 * (e.g. ColSpan=3 next to a ColSpan=1) can
                                 * still extend to the viewport edge. */
                                ...(carouselActive &&
                                !isFluid &&
                                hasPartialTilePeek &&
                                !contextDisableViewportBleed
                                    ? (() => {
                                          /* Per-breakpoint left bleed: only
                                           * suppress where the nested block
                                           * actually has a sibling to its
                                           * left at that breakpoint. When
                                           * `isFullWidthInParent[bp]` is
                                           * true the block spans the entire
                                           * parent row (e.g. a sibling is
                                           * hidden on mobile) and bleeding
                                           * is safe. The right bleed is
                                           * always applied — sibling on the
                                           * right is fine, the carousel
                                           * scrolls into that space rather
                                           * than across it. */
                                          const buildLeft = (px) => {
                                              if (isFullWidthInParent === true) return px
                                              const out = {}
                                              Object.entries(isFullWidthInParent).forEach(
                                                  ([bp, full]) => {
                                                      out[bp] = full ? px : 0
                                                  }
                                              )
                                              return out
                                          }
                                          return {
                                              marginLeft: buildLeft(`-${bleedLeft}px`),
                                              marginRight: `-${bleedRight}px`,
                                              paddingLeft: buildLeft(`${bleedLeft}px`),
                                              paddingRight: `${bleedRight}px`,
                                              scrollPaddingLeft: buildLeft(`${bleedLeft}px`),
                                              scrollPaddingRight: `${bleedRight}px`
                                          }
                                      })()
                                    : null),
                                /* When nested in a parent grid cell, grow
                                 * the scroll track to fill the wrapper's
                                 * height so a single inner tile (or a row
                                 * of stretched tiles) can be aligned
                                 * vertically inside the row, not just
                                 * top-anchored to its content height. */
                                ...(sectionGridColumn ? {flex: '1 1 auto', minHeight: 0} : null),
                                /* Delegate horizontal touch from any nested
                                 * product-tile image scroller to this
                                 * carousel — see `innerScrollerOverflowX`
                                 * above. */
                                '[data-pt-media-scroller="true"]': {
                                    overflowX: innerScrollerOverflowX
                                },
                                ...reverseSx
                            }}
                        >
                            {renderList.map((tile, idx) => {
                                const meta = slideMeta(idx, tile, cloneCount, limited.length, keyOf)
                                /* If the tile itself is hidden on every breakpoint we'd otherwise
                                 * render an empty slot here (still occupying width and counting as
                                 * a slide). Respect the child's HideBlockOn at the wrapper level
                                 * so it disappears entirely instead of leaving an empty column. */
                                const tileHide = getGDIHideStyles(
                                    tile?.HideBlockOn || tile?.HideOnDevices
                                )
                                if (tileHide.doNotLoadComponent) return null
                                /* Per-breakpoint display: 'none' where the tile is hidden, 'flex'
                                 * otherwise (wrapper still needs to be a flex column when visible
                                 * so its child can grow to fill the row height). */
                                const slideDisplay = tileHide.display
                                    ? Object.fromEntries(
                                          Object.entries(tileHide.display).map(([bp, v]) => [
                                              bp,
                                              v === 'none' ? 'none' : 'flex'
                                          ])
                                      )
                                    : 'flex'
                                /* Honour an authored `ColSpan` on the tile itself by widening
                                 * just this slide. The parent track is flex (not CSS Grid), so
                                 * a child's `gridColumn: span N` is silently ignored — instead
                                 * we compute the equivalent flex width here. Auto mode (no
                                 * authored TilesPerRow) keeps slides at their intrinsic content
                                 * width and ColSpan is meaningless in that case. */
                                const tileColSpan = tilesPerRowAuto ? null : getTileColSpan(tile)
                                const tileSlideWidth = tileColSpan
                                    ? buildSpanWidth(tileColSpan, tilesPerRow, tilesGap)
                                    : slideWidth
                                /* Apply fallback styles for tiles that have no
                                 * per-tile styles authored.
                                 * - ContentTilesStyles → GDIContentTileBlock
                                 * - CtaButtonStylesPerViewType → GDICtaBlock */
                                let effectiveTile = tile
                                if (
                                    tile?.__typename === 'GDIContentTileBlock' &&
                                    ContentTilesStyles?.length > 0 &&
                                    !(tile.GDIContentTileBlockStylesPerViewType?.length > 0)
                                ) {
                                    effectiveTile = {
                                        ...tile,
                                        GDIContentTileBlockStylesPerViewType: ContentTilesStyles
                                    }
                                } else if (
                                    tile?.__typename === 'GDICtaBlock' &&
                                    CtaButtonStylesPerViewType?.length > 0
                                ) {
                                    const tileStyles = tile.GDICtaBlockStylesPerViewType || []
                                    /* Merge parent CtaButtonStylesPerViewType with the tile's own
                                     * styles. For each ViewTypesToApply entry in the parent, tile
                                     * values win for non-empty fields; the parent fills in the rest.
                                     * Tile entries whose ViewTypesToApply has no parent counterpart
                                     * are kept as-is. */
                                    const parentViewTypes = new Set(
                                        CtaButtonStylesPerViewType.map((s) => s.ViewTypesToApply)
                                    )
                                    const mergedStyles = CtaButtonStylesPerViewType.map(
                                        (parentEntry) => {
                                            const tileEntry =
                                                tileStyles.find(
                                                    (s) =>
                                                        s.ViewTypesToApply ===
                                                        parentEntry.ViewTypesToApply
                                                ) || {}
                                            return Object.fromEntries(
                                                Object.entries(parentEntry).map(
                                                    ([key, parentVal]) => [
                                                        key,
                                                        tileEntry[key] != null &&
                                                        tileEntry[key] !== ''
                                                            ? tileEntry[key]
                                                            : parentVal
                                                    ]
                                                )
                                            )
                                        }
                                    )
                                    const extraTileStyles = tileStyles.filter(
                                        (s) => !parentViewTypes.has(s.ViewTypesToApply)
                                    )
                                    effectiveTile = {
                                        ...tile,
                                        GDICtaBlockStylesPerViewType: [
                                            ...mergedStyles,
                                            ...extraTileStyles
                                        ]
                                    }
                                }
                                return (
                                    <Box
                                        key={meta.key}
                                        data-real-index={
                                            meta.realIdx === null ? undefined : meta.realIdx
                                        }
                                        aria-hidden={meta.realIdx === null ? true : undefined}
                                        flex="0 0 auto"
                                        width={tileSlideWidth}
                                        display={slideDisplay}
                                        /* Make the wrapper a flex column and have its single child grow
                                         * to fill the available height. The parent Flex stretches each
                                         * wrapper to the tallest sibling's height (default
                                         * align-items: stretch); without this, the rendered child
                                         * (GDIContentTileBlock etc.) keeps its content height and
                                         * leaves empty space below. */
                                        sx={{
                                            scrollSnapAlign: 'start',
                                            flexDirection: 'column',
                                            /* Stretch every child EXCEPT CTAs to fill the slide
                                             * height (so content tiles match sibling heights).
                                             * CTAs are intrinsically sized — applying `flex: 1`
                                             * to them would also grow them vertically, breaking
                                             * the button's natural height. */
                                            '& > *:not([data-block-type="GDICtaBlock"])': {
                                                flex: 1,
                                                minHeight: 0
                                            },
                                            /* CTAs ship with `alignSelf: 'flex-start'` baked in
                                             * (see gdiCtaBlock) so they don't stretch to fill the
                                             * slide. That means the row-level `justify-content`
                                             * can't realign them when there's no slack along the
                                             * main axis (e.g. TilesPerRow=1). Override per-slide
                                             * here based on ContentJustification. */
                                            '& > [data-block-type="GDICtaBlock"]': {
                                                alignSelf: ctaAlignSelf,
                                                maxWidth: '100%'
                                            }
                                        }}
                                    >
                                        {renderComponentsSwitch(effectiveTile)}
                                    </Box>
                                )
                            })}
                        </Flex>

                        {showArrows && limited.length > 1 && carouselActive && (
                            <>
                                <ArrowButton
                                    label="Scroll left"
                                    onClick={() => scroll(-1)}
                                    side="left"
                                    hidden={!loop && atStart}
                                    icon={<ChevronLeftIcon color="black" />}
                                    sx={arrowSx}
                                    display={arrowDisplay}
                                />
                                <ArrowButton
                                    label="Scroll right"
                                    onClick={() => scroll(1)}
                                    side="right"
                                    hidden={!loop && atEnd}
                                    icon={<ChevronRightIcon color="black" />}
                                    sx={arrowSx}
                                    display={arrowDisplay}
                                />
                            </>
                        )}
                    </Box>
                </GDIProductTogglesContext.Provider>
            </GDIGridColumnsContext.Provider>
        </GDIHoverRevealContext.Provider>
    )
}

GDICarouselGridBlock.propTypes = {
    HideOnDevices: PropTypes.string,
    HideBlockOn: PropTypes.string,
    ContentTiles: PropTypes.array,
    CtaButtonDynamicContent: PropTypes.string,
    CtaButtonDynamicContentReference: PropTypes.string,
    CtaButtonManualContent: PropTypes.array,
    CtaButtonStylesPerViewType: PropTypes.array,
    ProductCarouselGridManualContent: PropTypes.array,
    ProductCarouselGridContentTile: PropTypes.array,
    ProductCarouselGridDynamicContent: PropTypes.string,
    ProductCarouselGridDynamicContentReference: PropTypes.string,
    GDICarouselGridBlockStylesPerViewType: PropTypes.array,
    ProductCarouselGridStylesPerViewType: PropTypes.array,
    ReverseOrderOnDesktop: PropTypes.bool,
    MaxItemsToShow: PropTypes.number,
    DisplayTextAndCTAsOnHover: PropTypes.bool,
    ContentTilesStyles: PropTypes.array,
    _recommendationData: PropTypes.object
}

export default GDICarouselGridBlock
